import React, { useState, useEffect } from 'react';
import { View, StyleSheet, ScrollView, Alert } from 'react-native';
import { Card, Text, Button, ActivityIndicator, TextInput, Modal, Portal } from 'react-native-paper';
import { colors, spacing, typography } from '../../constants/theme';
import { adminService } from '../../services/adminService';

interface VendorTypesScreenProps {
  onBack?: () => void;
}

interface VendorType {
  id: number;
  name: string;
  description?: string;
  emailTemplate?: string;
  displayOrder: number;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  vendors?: any[];
}

interface VendorTypeFormData {
  name: string;
  description: string;
  emailTemplate: string;
}

const getDefaultEmailTemplate = (vendorTypeName: string): string => {
  const defaultTemplates: Record<string, string> = {
    'Landscaping': `Dear [Vendor Name],

[Company Name] recently completed a comprehensive home review at the following property:

[Property Address]

Based on our assessment, we identified several landscaping needs that require professional attention:

[Home specific details go here]

We would appreciate the opportunity to discuss these landscaping requirements with you. If you're interested in providing a quote or would like to schedule a site visit, please contact us at your earliest convenience.

Best regards,
The [Company Name] Team
admin@exhale.com`,
    'Pool Maintenance': `Dear [Vendor Name],

[Company Name] has completed a detailed review of a property with pool facilities that require professional maintenance services:

[Property Address]

Our inspection identified the following pool-related items that need attention:

[Home specific details go here]

We would be grateful for your expertise in addressing these pool maintenance needs. Please let us know if you're available to provide a quote or consultation.

Best regards,
The [Company Name] Team
admin@exhale.com`,
  };
  
  return defaultTemplates[vendorTypeName] || `Dear [Vendor Name],

[Company Name] recently conducted a property review that identified ${vendorTypeName.toLowerCase()} service needs:

[Property Address]

Our assessment revealed the following items requiring attention:

[Home specific details go here]

We would appreciate your professional evaluation and quote for addressing these needs. Please contact us if you're interested in providing services for this property.

Best regards,
The [Company Name] Team
admin@exhale.com`;
};

export const VendorTypesScreen: React.FC<VendorTypesScreenProps> = ({ onBack }) => {
  const [vendorTypes, setVendorTypes] = useState<VendorType[]>([]);
  const [loading, setLoading] = useState(true);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingType, setEditingType] = useState<VendorType | null>(null);
  const [formData, setFormData] = useState<VendorTypeFormData>({
    name: '',
    description: '',
    emailTemplate: ''
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    loadVendorTypes();
  }, []);

  const loadVendorTypes = async () => {
    try {
      setLoading(true);
      const vendorTypesData = await adminService.listVendorTypes<VendorType>();
      setVendorTypes(vendorTypesData || []);
    } catch (error) {
      console.error('Error loading vendor types:', error);
      Alert.alert('Error', 'Failed to load vendor types');
      setVendorTypes([]);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateVendorType = () => {
    setEditingType(null);
    setFormData({ name: '', description: '', emailTemplate: '' });
    setModalVisible(true);
  };

  const handleEditVendorType = (type: VendorType) => {
    setEditingType(type);
    setFormData({ 
      name: type.name, 
      description: type.description || '',
      emailTemplate: type.emailTemplate || getDefaultEmailTemplate(type.name)
    });
    setModalVisible(true);
  };

  const handleSubmitForm = async () => {
    if (!formData.name.trim()) {
      Alert.alert('Error', 'Vendor type name is required');
      return;
    }

    try {
      setSubmitting(true);
      
      if (editingType) {
        // Update existing vendor type
        await adminService.updateVendorType(editingType.id, formData);
        Alert.alert('Success', 'Vendor type updated successfully');
      } else {
        // Create new vendor type
        await adminService.createVendorType(formData);
        Alert.alert('Success', 'Vendor type created successfully');
      }
      
      setModalVisible(false);
      await loadVendorTypes(); // Refresh the list
    } catch (error) {
      console.error('Error saving vendor type:', error);
      Alert.alert('Error', 'Failed to save vendor type');
    } finally {
      setSubmitting(false);
    }
  };

  const handleToggleVendorTypeStatus = async (type: VendorType) => {
    const action = type.isActive ? 'deactivate' : 'activate';
    Alert.alert(
      `${action === 'deactivate' ? 'Deactivate' : 'Activate'} Vendor Type`,
      `Are you sure you want to ${action} ${type.name}?`,
      [
        { text: 'Cancel', style: 'cancel' },
        {
          text: action === 'deactivate' ? 'Deactivate' : 'Activate',
          style: action === 'deactivate' ? 'destructive' : 'default',
          onPress: async () => {
            try {
              await adminService.updateVendorType(type.id, {
                isActive: !type.isActive
              });
              await loadVendorTypes(); // Refresh the list
              Alert.alert('Success', `Vendor type ${action}d successfully`);
            } catch (error) {
              console.error(`Error ${action}ing vendor type:`, error);
              Alert.alert('Error', `Failed to ${action} vendor type`);
            }
          }
        }
      ]
    );
  };

  const renderVendorTypeCard = (type: VendorType) => (
    <Card key={type.id} style={[styles.card, !type.isActive && styles.inactiveCard]}>
      <Card.Content>
        <View style={styles.typeHeader}>
          <View style={styles.typeInfo}>
            <Text style={styles.typeName}>{type.name}</Text>
            {type.description && (
              <Text style={styles.typeDescription}>{type.description}</Text>
            )}
            <View style={styles.typeMetadata}>
              <Text style={styles.metadata}>
                Vendors: {type.vendors?.length || 0}
              </Text>
              {type.emailTemplate && (
                <Text style={styles.metadata}>
                  • Email template configured
                </Text>
              )}
            </View>
          </View>
          <View style={styles.typeActions}>
            <Button
              mode="outlined"
              onPress={() => handleEditVendorType(type)}
              style={styles.editButton}
            >
              Edit
            </Button>
            <Button
              mode="outlined"
              onPress={() => handleToggleVendorTypeStatus(type)}
              style={type.isActive ? styles.deactivateButton : styles.activateButton}
            >
              {type.isActive ? 'Deactivate' : 'Activate'}
            </Button>
          </View>
        </View>
        
        <View style={styles.typeStats}>
          <Text style={styles.stat}>
            Status: {type.isActive ? 'Active' : 'Inactive'}
          </Text>
          <Text style={styles.stat}>
            Created: {new Date(type.createdAt).toLocaleDateString()}
          </Text>
        </View>
      </Card.Content>
    </Card>
  );

  if (loading) {
    return (
      <View testID="admin-vendor-types-screen" style={styles.loading}>
        <ActivityIndicator size="large" />
        <Text style={styles.loadingText}>Loading vendor types...</Text>
      </View>
    );
  }

  return (
    <View testID="admin-vendor-types-screen" style={styles.container}>
      <ScrollView testID="admin-vendor-types-scroll" style={styles.scrollView}>
        <Card style={styles.headerCard}>
          <Card.Content>
            {onBack && (
              <View style={styles.backButtonContainer}>
                <Button
                  testID="admin-back-button"
                  mode="text"
                  onPress={onBack}
                  style={styles.backButton}
                  icon={() => <Text style={styles.backIcon}>←</Text>}
                >
                  Back to Admin Menu
                </Button>
              </View>
            )}
            <Text style={styles.title}>Vendor Type Management</Text>
            <Text style={styles.subtitle}>Manage categories and types of vendors</Text>
          </Card.Content>
        </Card>

        <View style={styles.sectionHeader}>
          <Text style={styles.sectionTitle}>Vendor Types ({vendorTypes.length})</Text>
          <Button
            testID="admin-create-vendor-type-button"
            mode="contained"
            onPress={handleCreateVendorType}
            style={styles.createButton}
          >
            + New Type
          </Button>
        </View>
        
        {vendorTypes.length > 0 ? (
          vendorTypes.map(renderVendorTypeCard)
        ) : (
          <Card style={styles.emptyCard}>
            <Card.Content>
              <Text style={styles.emptyText}>No vendor types found</Text>
              <Text style={styles.emptySubtext}>Create vendor types to categorize your vendors</Text>
            </Card.Content>
          </Card>
        )}
      </ScrollView>

      {/* Create/Edit Modal */}
      <Portal>
        <Modal
          visible={modalVisible}
          onDismiss={() => setModalVisible(false)}
          contentContainerStyle={styles.modalContainer}
        >
          <Card>
            <Card.Content>
              <Text style={styles.modalTitle}>
                {editingType ? 'Edit Vendor Type' : 'Create New Vendor Type'}
              </Text>
              
              <TextInput
                label="Name *"
                value={formData.name}
                onChangeText={(text) => setFormData(prev => ({ ...prev, name: text }))}
                style={styles.input}
                mode="outlined"
                placeholder="e.g., Power Washing"
              />
              
              <TextInput
                label="Description"
                value={formData.description}
                onChangeText={(text) => setFormData(prev => ({ ...prev, description: text }))}
                style={styles.input}
                mode="outlined"
                multiline
                numberOfLines={3}
                placeholder="Brief description of this vendor type..."
              />
              
              <TextInput
                label="Email Template"
                value={formData.emailTemplate}
                onChangeText={(text) => setFormData(prev => ({ ...prev, emailTemplate: text }))}
                style={[styles.input, styles.emailTemplateInput]}
                mode="outlined"
                multiline
                numberOfLines={10}
                placeholder="Email template with placeholders:
[Company Name] - Will be replaced with company name
[Property Address] - Will be replaced with property address
[Home specific details go here] - Will be replaced with AI-generated content"
              />
              
              <View style={styles.modalActions}>
                <Button
                  mode="outlined"
                  onPress={() => setModalVisible(false)}
                  style={styles.cancelButton}
                  disabled={submitting}
                >
                  Cancel
                </Button>
                <Button
                  mode="contained"
                  onPress={handleSubmitForm}
                  style={styles.submitButton}
                  loading={submitting}
                  disabled={submitting}
                >
                  {editingType ? 'Update' : 'Create'}
                </Button>
              </View>
            </Card.Content>
          </Card>
        </Modal>
      </Portal>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: colors.background,
  },
  scrollView: {
    flex: 1,
    padding: spacing.md,
  },
  loading: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: colors.background,
  },
  loadingText: {
    ...typography.body,
    marginTop: spacing.md,
    color: colors.textSecondary,
  },
  headerCard: {
    marginBottom: spacing.lg,
  },
  backButtonContainer: {
    marginBottom: spacing.md,
  },
  backButton: {
    alignSelf: 'flex-start',
    marginLeft: -spacing.sm,
  },
  backIcon: {
    fontSize: 18,
    color: colors.primary,
  },
  title: {
    ...typography.h1,
    marginBottom: spacing.sm,
  },
  subtitle: {
    ...typography.body,
    color: colors.textSecondary,
    marginBottom: spacing.md,
  },
  sectionHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: spacing.md,
  },
  sectionTitle: {
    ...typography.h2,
  },
  createButton: {
    // Custom styling for create button
  },
  card: {
    marginBottom: spacing.md,
  },
  inactiveCard: {
    backgroundColor: colors.surface,
    borderLeftWidth: 4,
    borderLeftColor: colors.error,
    opacity: 0.7,
  },
  typeHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
  },
  typeInfo: {
    flex: 1,
    marginRight: spacing.md,
  },
  typeName: {
    ...typography.h3,
    marginBottom: spacing.xs,
  },
  typeDescription: {
    ...typography.body,
    color: colors.textSecondary,
    marginBottom: spacing.sm,
  },
  typeMetadata: {
    flexDirection: 'row',
    gap: spacing.md,
    marginBottom: spacing.sm,
  },
  metadata: {
    ...typography.caption,
    color: colors.primary,
    fontWeight: '600',
  },
  typeActions: {
    flexDirection: 'row',
    gap: spacing.sm,
  },
  editButton: {
    borderColor: colors.primary,
    alignItems: 'center',
    justifyContent: 'center',
  },
  deactivateButton: {
    borderColor: colors.error,
    alignItems: 'center',
    justifyContent: 'center',
  },
  activateButton: {
    borderColor: colors.primary,
    alignItems: 'center',
    justifyContent: 'center',
  },
  typeStats: {
    flexDirection: 'row',
    marginTop: spacing.md,
    gap: spacing.md,
  },
  stat: {
    ...typography.caption,
    color: colors.textSecondary,
  },
  emptyCard: {
    marginTop: spacing.xl,
  },
  emptyText: {
    ...typography.h3,
    textAlign: 'center',
    marginBottom: spacing.sm,
    color: colors.textSecondary,
  },
  emptySubtext: {
    ...typography.body,
    textAlign: 'center',
    color: colors.textSecondary,
  },
  modalContainer: {
    margin: spacing.lg,
  },
  modalTitle: {
    ...typography.h2,
    marginBottom: spacing.lg,
    textAlign: 'center',
  },
  input: {
    marginBottom: spacing.md,
  },
  modalActions: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginTop: spacing.lg,
    gap: spacing.md,
  },
  cancelButton: {
    flex: 1,
  },
  submitButton: {
    flex: 1,
  },
  emailTemplateInput: {
    minHeight: 200,
  },
});

export default VendorTypesScreen;
